// Chamadas (telefonia) e os relatórios que saem DIRETO delas: a listagem, o
// resumo agregado, a taxa de conversão do CRM e o sucesso por tentativa.
// Um módulo por domínio do back — a fronteira HTTP continua sendo uma só
// (./client.js), só deixou de ser um arquivo único de 4.4k linhas.
//
// Fronteira deste módulo: o que o back serve em /v1/calls, /v1/conversions,
// /v1/conversion-stages e o /v1/access/exchange que abre a sessão temporária do
// click-to-call (a credencial existe para DISCAR — sem ela não há chamada).
// Ficou de fora, de propósito, o catálogo /v1/crm/stages: ele é o inventário de
// etapas do HubSpot, consumido pelas telas de lead/integração, e vai para o
// módulo do CRM na onda dele — trazê-lo para cá só o faria mudar de casa duas
// vezes. O mapeamento etapa → CONVERSION/LOSS (/v1/conversion-stages) fica
// aqui: é a régua que decide o que a taxa de conversão conta.

import {
  activeTemporaryAccessToken,
  api,
  API_BASE_URL,
  setTemporaryAccessToken,
  toApiError,
} from "./client.js";

// --- Chamadas (telefonia) ---
// Espelha a feature `sip-call` do back (/v1/calls). Datas chegam como string ISO.

export const CALL_STATUSES = [
  "RINGING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "NO_ANSWER",
] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];

// Rótulos em português para exibição (UI toda em pt-br).
export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  RINGING: "Chamando",
  IN_PROGRESS: "Em andamento",
  COMPLETED: "Completada",
  FAILED: "Falhou",
  NO_ANSWER: "Não atendida",
};

// Motivos de falha (call.failureReason) → rótulo pt-br. Fonte da verdade dos
// códigos: ari-worker/src/hangup-cause.ts (classifyHangupCause) + os setados no
// orchestrator (originate_error/ai_media_unreachable/no_target). MANTER EM
// SINCRONIA com o worker: um código novo lá precisa de entrada aqui.
export const CALL_FAILURE_LABELS: Record<string, string> = {
  carrier_congestion: "Operadora congestionada, tente novamente",
  carrier_unavailable: "Operadora indisponível no momento",
  invalid_number: "Número inválido ou inexistente",
  busy: "Ocupado",
  rejected: "Chamada rejeitada",
  no_answer: "Não atendida",
  voicemail: "Caixa postal",
  originate_error: "Falha ao originar no tronco",
  ai_media_unreachable: "IA inacessível (túnel/VPN)",
  no_target: "Número inválido",
  // Não vem do worker: é o reaper de ligação fantasma do back que grava este
  // motivo ao fechar uma `call` que ninguém finalizou. Ver
  // back/src/features/sip-call/ghost-call.ts.
  not_finalized: "Não finalizada pela telefonia",
};

// Traduz um failureReason para pt-br; cai no próprio código se desconhecido.
export function callFailureLabel(failureReason: string | null | undefined): string | null {
  if (!failureReason) return null;
  return CALL_FAILURE_LABELS[failureReason] ?? failureReason;
}

// Status terminais de uma chamada (para parar o polling ao vivo).
export function isTerminalStatus(s?: CallStatus): boolean {
  return s === "COMPLETED" || s === "FAILED" || s === "NO_ANSWER";
}

export const CALL_DIRECTIONS = ["OUTBOUND", "INBOUND"] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

export const CALL_DIRECTION_LABELS: Record<CallDirection, string> = {
  OUTBOUND: "Saída",
  INBOUND: "Entrada",
};

// Linha de chamada retornada por GET /v1/calls, com o agente enriquecido.
export interface CallListItem {
  id: string;
  organizationId: string;
  assignedTo: string | null;
  direction: CallDirection;
  status: CallStatus;
  targetPhone: string | null;
  // Número mascarado por papel: o vendedor não vê o número discado no histórico.
  targetPhoneHidden: boolean;
  callerDid: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** Tempo FALADO em segundos — conta do atendimento ao desligar. */
  duration: number | null;
  /** Tempo em ESPERA em segundos: do pedido de discagem até atenderem.
   *  Null quando ninguém atendeu (não houve espera *até* o atendimento). */
  waitSeconds: number | null;
  recordingName: string | null;
  failureReason: string | null;
  notes: string | null;
  dispositionId: string | null;
  createdAt: string;
  agentName: string | null;
  agentEmail: string | null;
  // Lead que recebeu a ligação (join no back por call.lead_id). Null quando a
  // chamada não tem lead interno: inbound, discagem manual antiga, click-to-call
  // efêmero do parceiro.
  leadId: string | null;
  leadName: string | null;
  // Etapa comercial ATUAL do lead (não a etapa da época da ligação) — o mesmo
  // texto que GET /v1/conversion-stages classifica em CONVERSION/LOSS. Null
  // sem lead (inbound, discagem manual, click-to-call) ou lead sem etapa.
  leadStage: string | null;
}

export interface ListCallsResponse {
  calls: CallListItem[];
  total: number;
  limit: number;
  offset: number;
}

// Filtros da listagem (viram query string). `active` = RINGING+IN_PROGRESS.
export interface CallsQuery {
  status?: CallStatus;
  direction?: CallDirection;
  assignedTo?: string;
  active?: boolean;
  /**
   * true = só as ligações originadas na tela de discagem automática (/dialer),
   * humanas e IA. Deixa de fora click-to-call, softphone manual, acesso
   * temporário e campanha. Omitido = todas.
   */
  predictive?: boolean;
  /** true = só ligações originadas pelo click-to-call. */
  clickToCall?: boolean;
  from?: string;
  to?: string;
  /** Tempo falado em segundos; não inclui o tempo de toque. */
  minDuration?: number;
  maxDuration?: number;
  limit?: number;
  offset?: number;
}

// GET /v1/calls — lista/filtra as chamadas da org (escopo por papel no back).
export async function getCalls(params: CallsQuery = {}): Promise<ListCallsResponse> {
  const { data, error } = await api<ListCallsResponse>("/v1/calls", { query: params });
  if (error) throw toApiError("GET /v1/calls", error);
  return data;
}

export interface CallSummaryAgent {
  userId: string | null;
  agentName: string | null;
  total: number;
  completed: number;
  totalDurationSec: number;
  /** Espera acumulada (segundos) das chamadas que chegaram a ser atendidas. */
  totalWaitSec: number;
  /** Quantas chamadas do agente foram atendidas — divisor da espera média. */
  answered: number;
}

export interface CallSummary {
  totalCalls: number;
  byStatus: { status: CallStatus; count: number }[];
  // Motivos de falha (tela de Saúde das ligações): só chamadas FAILED, agrupadas
  // por failureReason (null = sem causa classificada), do mais ao menos frequente.
  byFailureReason: { reason: string | null; count: number }[];
  byAgent: CallSummaryAgent[];
  // Séries por dia dos gráficos, já agregadas no back (fuso America/Sao_Paulo).
  // Esparso: só dias com chamada — o dashboard preenche os dias vazios do range.
  // completed/failed/noAnswer = desfechos do dia (tela de Saúde das ligações).
  perDay: {
    day: string;
    total: number;
    completed: number;
    failed: number;
    noAnswer: number;
    avgSec: number | null;
  }[];
  // Série por HORA do "ritmo de hoje" do Dashboard. `hour` é o início da hora
  // LOCAL da operação em ISO sem timezone ("2026-08-18T10:00") — rótulo pronto,
  // não reconverta para Date. Esparso, igual ao perDay.
  //
  // Opcional de propósito: back antigo não manda o campo. Front na frente do
  // deploy do back mostra o ritmo zerado, não uma tela quebrada.
  perHour?: {
    hour: string;
    total: number;
    completed: number;
  }[];
}

export interface CallSummaryQuery {
  from?: string;
  to?: string;
  assignedTo?: string;
  direction?: CallDirection;
  status?: CallStatus;
  minDuration?: number;
  maxDuration?: number;
  /** Ver CallsQuery.predictive. */
  predictive?: boolean;
  /** true = só ligações originadas pelo click-to-call. */
  clickToCall?: boolean;
}

// GET /v1/calls/summary — agregações do período (dashboard/relatório de agentes).
export async function getCallsSummary(params: CallSummaryQuery = {}): Promise<CallSummary> {
  const { data, error } = await api<CallSummary>("/v1/calls/summary", { query: params });
  if (error) throw toApiError("GET /v1/calls/summary", error);
  return data;
}

// --- Taxa de conversão CRM ---
// workedLeads = denominador (leads distintos com >=1 ligação no período);
// rate já vem arredondado do back, em pontos percentuais (40 = 40%).
export interface ConversionMetrics {
  workedLeads: number;
  conversions: number;
  losses: number;
  rate: number;
}

export interface ConversionSummary {
  totals: ConversionMetrics;
  // day: YYYY-MM-DD no fuso America/Sao_Paulo (agregado no back).
  byDay: (ConversionMetrics & { day: string })[];
  bySeller: (ConversionMetrics & { userId: string | null })[];
  byOrganization: (ConversionMetrics & { organizationId: string })[];
}

export interface ConversionsSummaryQuery {
  from?: string;
  to?: string;
  assignedTo?: string;
  /** Ver CallsQuery.predictive — o mesmo recorte, aplicado à ligação-âncora. */
  predictive?: boolean;
}

// GET /v1/conversions/summary — agregações de conversão do período (relatório/dashboard).
export async function getConversionsSummary(
  params: ConversionsSummaryQuery = {},
): Promise<ConversionSummary> {
  const { data, error } = await api<ConversionSummary>("/v1/conversions/summary", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/conversions/summary", error);
  return data;
}

// --- Sucesso por tentativa (Relatório de Vendedores) ---
// Das ligações que foram a N-ésima tentativa daquele lead: quantas atenderam
// (`answered`) e quantas ancoraram uma conversão (`conversions`). `calls` é o
// denominador das DUAS taxas. Balde sem ligação não vem na lista.
export interface AttemptBucket {
  attempt: number;
  calls: number;
  answered: number;
  conversions: number;
}

export interface AttemptSuccessSummary {
  /** Último balde do eixo; ele agrega tudo dali para cima ("8+"). */
  maxAttempt: number;
  totals: AttemptBucket[];
  bySeller: { userId: string | null; buckets: AttemptBucket[] }[];
}

// GET /v1/conversions/attempts — sucesso por tentativa. Mesma query do resumo.
export async function getConversionsByAttempt(
  params: ConversionsSummaryQuery = {},
): Promise<AttemptSuccessSummary> {
  const { data, error } = await api<AttemptSuccessSummary>("/v1/conversions/attempts", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/conversions/attempts", error);
  return data;
}

export type ConversionStageResult = "CONVERSION" | "LOSS";

export interface ConversionStage {
  id: string;
  stage: string;
  result: ConversionStageResult;
}

export interface ConversionStagesResponse {
  stages: ConversionStage[];
}

// GET /v1/conversion-stages — mapeamento etapa do HubSpot -> CONVERSION/LOSS, configurado pela org.
export async function getConversionStages(): Promise<ConversionStagesResponse> {
  const { data, error } = await api<ConversionStagesResponse>("/v1/conversion-stages");
  if (error) throw toApiError("GET /v1/conversion-stages", error);
  return data;
}

// PUT /v1/conversion-stages — substitui o conjunto inteiro (gestor+; vendedor recebe 403).
// 422 (conversion_stage_invalid) se alguma etapa vier em branco.
export async function putConversionStages(
  stages: { stage: string; result: ConversionStageResult }[],
): Promise<ConversionStagesResponse> {
  const { data, error } = await api<ConversionStagesResponse>("/v1/conversion-stages", {
    method: "PUT",
    body: { stages },
  });
  if (error) throw toApiError("PUT /v1/conversion-stages", error);
  return data;
}

export interface ConversionStageOptionsResponse {
  options: { stage: string; count: number }[];
}

// GET /v1/conversion-stages/options — etapas que o HubSpot já mandou para a org
// (da mais frequente para a menos), para alimentar o combo de configuração.
export async function getConversionStageOptions(): Promise<ConversionStageOptionsResponse> {
  const { data, error } = await api<ConversionStageOptionsResponse>(
    "/v1/conversion-stages/options",
  );
  if (error) throw toApiError("GET /v1/conversion-stages/options", error);
  return data;
}

// CallView — detalhe de UMA chamada (GET /v1/calls/:callId). É o CallListItem
// SEM os campos de agente (só a listagem os enriquece).
export interface CallView {
  id: string;
  organizationId: string;
  assignedTo: string | null;
  direction: CallDirection;
  status: CallStatus;
  targetPhone: string | null;
  // Número mascarado por papel: o vendedor não vê o número discado no histórico.
  targetPhoneHidden: boolean;
  callerDid: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** Tempo FALADO em segundos — conta do atendimento ao desligar. */
  duration: number | null;
  /** Tempo em ESPERA em segundos: do pedido de discagem até atenderem. */
  waitSeconds: number | null;
  recordingName: string | null;
  failureReason: string | null;
  notes: string | null;
  dispositionId: string | null;
  createdAt: string;
}

// Entrada do click-to-call. targetPhone: só dígitos (o back normaliza o 9º
// dígito e prepende o tech-prefix). sellerSipUsername/callerDid são opcionais —
// o back resolve pelo ramal do usuário logado e pelo DID da org.
export interface CreateCallInput {
  // Exatamente UM: targetPhone (discagem direta — /phone) OU leadId (o back
  // resolve o número do lead; o vendedor não vê o telefone — usado no discador).
  targetPhone?: string;
  leadId?: string;
  sellerSipUsername?: string;
  callerDid?: string;
  // Motivo da discagem manual (obrigatório quando a marca liga o toggle
  // manualDialEnabled — ver getManualDial/setManualDial).
  reason?: string;
}

// Alvo da concessão de acesso temporário (telas /call e /click-to-call-hubspot).
// `lead.name` é o nome de quem vai ser chamado, quando o conhecemos; `phone` é o
// número. A discagem em si continua por referência no back — o navegador nunca
// escolhe o número.
export interface TempAccessSession {
  accessToken: string;
  lead: { id: string | null; name: string | null };
  phone: string | null;
  seller: { name: string | null };
  // Identidade visual da marca dona do link (white label). O console público
  // abre dentro do CRM do cliente, para um vendedor que nem sempre conhece o
  // Disk: logo e cor são os da marca, não os do produto. Opcional porque o
  // campo é novo — um back mais antigo simplesmente não manda, e a tela cai no
  // visual do produto.
  brand?: { name: string; logo: string | null; color: string | null };
  // Modo fila: presente ⇒ a tela disca por /v1/dialer/dial e lê o desfecho por
  // /v1/dialer/lead-outcome — quem tabula e avança o campaign_lead (attempts,
  // reagendamento, teto) é a auto-tabulação do back. Nulo ⇒ click-to-call por
  // telefone, como sempre foi.
  campaignLeadId: string | null;
}

// POST /v1/access/exchange — troca o token da URL pela sessão temporária. O back
// mantém o cookie para uso standalone e devolve uma credencial curta para iframe;
// ela fica somente em memória e acompanha as chamadas seguintes como Bearer.
export async function exchangeTempAccess(token: string): Promise<TempAccessSession> {
  const { data, error } = await api<TempAccessSession>("/v1/access/exchange", {
    method: "POST",
    body: { token },
  });
  if (error) throw toApiError("POST /v1/access/exchange", error);
  setTemporaryAccessToken(data.accessToken);
  return data;
}

// POST /v1/calls — origina a chamada no servidor (click-to-call). O back liga a
// perna A no ramal do vendedor (o softphone atende o INVITE) e a perna B no lead.
export async function createCall(input: CreateCallInput): Promise<{ callId: string }> {
  const { data, error } = await api<{ callId: string }>("/v1/calls", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/calls", error);
  return data;
}

// POST /v1/calls/ai — dispara UMA ligação com a IA. O back resolve o DID e monta
// o trunkTarget; a IA fala via AudioSocket. `supervised` = o agente da sessão OUVE
// a IA↔lead (perna WebRTC mutada) e pode assumir; sem ele = fire-and-forget.
export async function startAiCall(input: {
  toNumber?: string;
  leadId?: string;
  callerDid?: string;
  supervised?: boolean;
}): Promise<{ callId: string }> {
  const { data, error } = await api<{ callId: string }>("/v1/calls/ai", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/calls/ai", error);
  return data;
}

// GET /v1/calls/:callId/recording — URLs temporárias para tocar e baixar. A
// downloadUrl é opcional durante a janela em que o front novo encontra back antigo.
export async function getCallRecordingUrl(
  callId: string,
): Promise<{ url: string; downloadUrl?: string }> {
  const { data, error } = await api<{ url: string; downloadUrl?: string }>(
    `/v1/calls/${callId}/recording`,
  );
  if (error) throw toApiError(`GET /v1/calls/${callId}/recording`, error);
  return data;
}

// DELETE /v1/calls/:callId/recording — remove apenas o WAV; ligação e
// transcrição permanecem disponíveis no histórico.
export async function deleteCallRecording(callId: string): Promise<void> {
  const { error } = await api(`/v1/calls/${callId}/recording`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/calls/${callId}/recording`, error);
}

// GET /v1/calls/:callId/transcription — transcrição diarizada da gravação (turnos
// [Agente]/[Lead]). Buscada sob demanda ao abrir o detalhe; null = ainda sem texto.
export async function getCallTranscription(
  callId: string,
): Promise<{ transcription: string | null }> {
  const { data, error } = await api<{ transcription: string | null }>(
    `/v1/calls/${callId}/transcription`,
  );
  if (error) throw toApiError(`GET /v1/calls/${callId}/transcription`, error);
  return data;
}

// GET /v1/calls/:callId — estado atual da chamada (usado no polling durante a ligação).
export async function getCall(callId: string): Promise<CallView> {
  const { data, error } = await api<CallView>(`/v1/calls/${callId}`);
  if (error) throw toApiError(`GET /v1/calls/${callId}`, error);
  return data;
}

// POST /v1/calls/:callId/terminate — encerra a chamada (o back destrói o bridge no ARI).
export async function terminateCall(callId: string): Promise<{ terminated: boolean }> {
  const { data, error } = await api<{ terminated: boolean }>(`/v1/calls/${callId}/terminate`, {
    method: "POST",
  });
  if (error) throw toApiError(`POST /v1/calls/${callId}/terminate`, error);
  return data;
}

// Mesma rota de terminate, disparada no `pagehide` (recarregar/fechar a aba).
// `fetch` normal seria ABORTADO junto com o documento; `keepalive` é o que deixa
// o POST sair mesmo com a página morrendo. Não dá para usar o better-fetch aqui
// (ele não expõe a flag), nem esperar/tratar a resposta: não há mais página a
// quem reportar. Melhor esforço — a rede pode cair, mas no caso comum (F5) o
// back encerra o bridge e a chamada não fica fantasma.
export function terminateCallOnUnload(callId: string): void {
  const bearer = activeTemporaryAccessToken();
  void fetch(`${API_BASE_URL}/v1/calls/${callId}/terminate`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: {
      Accept: "application/json",
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
  }).catch(() => {});
}

// POST /v1/calls/:callId/takeover — assume a ligação IA: a IA fala a despedida e
// encerra a perna, e o agente (que ouvia) assume o lead.
export async function takeoverCall(callId: string): Promise<{ takeover: boolean }> {
  const { data, error } = await api<{ takeover: boolean }>(`/v1/calls/${callId}/takeover`, {
    method: "POST",
  });
  if (error) throw toApiError(`POST /v1/calls/${callId}/takeover`, error);
  return data;
}

// PATCH /v1/calls/:callId/notes — anota a chamada (pós-atendimento).
export async function updateCallNotes(
  callId: string,
  notes: string,
): Promise<{ updated: boolean }> {
  const { data, error } = await api<{ updated: boolean }>(`/v1/calls/${callId}/notes`, {
    method: "PATCH",
    body: { notes },
  });
  if (error) throw toApiError(`PATCH /v1/calls/${callId}/notes`, error);
  return data;
}
